import { useState, useEffect } from 'react';
import { adminApi } from '../../api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ShoppingBag, Loader2, Search, X } from 'lucide-react';
import Pagination from '../../components/Pagination';

const formatMoney = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);
const formatDate = (date) => new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const PER_PAGE = 15;

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewAll, setViewAll] = useState(false);

  useEffect(() => { fetchOrders(); }, []);

  useEffect(() => {
    let filtered = allOrders;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = allOrders.filter(order => 
        order.username?.toLowerCase().includes(query) || 
        order.id.toLowerCase().includes(query)
      );
    }
    
    setOrders(filtered);
    setCurrentPage(1);
  }, [searchQuery, allOrders]);

  const fetchOrders = async () => {
    try { 
      const response = await adminApi.getOrders({ per_page: 1000 }); 
      setAllOrders(response.data.data || []);
      setOrders(response.data.data || []);
    } catch (error) { console.error('Failed to fetch orders:', error); } 
    finally { setIsLoading(false); }
  };

  // Pagination logic
  const totalItems = orders.length;
  const totalPages = viewAll ? 1 : Math.ceil(totalItems / PER_PAGE);
  const displayedOrders = viewAll 
    ? orders 
    : orders.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-white font-['Outfit']">Orders ({totalItems})</h1>
        
        {/* Search Bar */}
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search by username or order ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 bg-[#121212] border-white/10 text-white placeholder:text-zinc-500"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {viewAll && totalItems > PER_PAGE && (
        <div className="flex items-center justify-between bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-lg p-3">
          <span className="text-sm text-[#FFD700]">Viewing all {totalItems} orders</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { setViewAll(false); setCurrentPage(1); }}
            className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10"
          >
            Show Paginated
          </Button>
        </div>
      )}

      {orders.length === 0 ? (
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-8 text-center">
            <ShoppingBag className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">{searchQuery ? `No orders found matching "${searchQuery}"` : 'No orders yet'}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {displayedOrders.map((order) => (
              <Card key={order.id} className="bg-[#0A0A0A] border-white/10 hover:border-[#FFD700]/30 transition-colors">
                <CardContent className="p-4 flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#121212] rounded-lg flex items-center justify-center">
                      <ShoppingBag className="w-5 h-5 text-[#FFD700]" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">#{order.id.slice(0, 8)}</p>
                      <p className="text-sm text-zinc-500">{order.username} · {formatDate(order.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-zinc-500">{order.items_count} item(s)</p>
                      <p className="font-bold text-[#FFD700]">{formatMoney(order.total_amount)}</p>
                    </div>
                    <Badge variant="outline" className={`${
                      order.status === 'completed' ? 'border-green-500/30 text-green-500' :
                      order.status === 'pending' ? 'border-yellow-500/30 text-yellow-500' :
                      'border-red-500/30 text-red-500'
                    }`}>{order.status}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {!viewAll && totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              perPage={PER_PAGE}
              onPageChange={setCurrentPage}
              onViewAll={() => setViewAll(true)}
            />
          )}
        </>
      )}
    </div>
  );
}
